import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Field, Pick } from "../loans/LoanFormDialog";
import { Loans } from "../../lib/api";
import { formatINR, formatIndianDate } from "../../lib/format";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

const today = () => {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};
const MODES = [{ value: "cash", label: "Cash" }, { value: "upi", label: "UPI" }, { value: "bank", label: "Bank Transfer" }, { value: "other", label: "Other" }];
const EMPTY = { clf_id: "", vo_id: "", shg_id: "", loan_id: "", payment_date: today(), amount: "", mode: "cash",
  reference: "", collected_by: "", remarks: "", override: false, override_reason: "", authorised_by: "" };

const Stat = ({ label, value, testId, warn }) => (
  <div><div className="text-[11px] text-emerald-900/60">{label}</div>
    <div className={`font-mono-tabular text-sm font-bold ${warn ? "text-rose-700" : "text-emerald-950"}`} data-testid={testId}>{value}</div></div>
);

export const RepaymentFormDialog = ({ open, onOpenChange, refs, loans, onSaved, onSubmit }) => {
  const { clfs, vos, shgs, members } = refs;
  const [v, setV] = useState(EMPTY);
  const [key, setKey] = useState("");
  const [sched, setSched] = useState(null);
  const [schedLoading, setSchedLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setV(EMPTY); setErrors({}); setSched(null); setKey(crypto.randomUUID());
  }, [open]);

  useEffect(() => {
    if (!v.loan_id) { setSched(null); return; }
    setSchedLoading(true);
    Loans.schedule(v.loan_id).then(setSched).catch(() => toast.error("Could not load loan schedule")).finally(() => setSchedLoading(false));
  }, [v.loan_id]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const opts = (list) => list.map((x) => ({ value: x.id, label: `${x.name} (${x.code || x.registration_number})` }));
  const voOpts = useMemo(() => opts(vos.filter((x) => x.clf_id === v.clf_id)), [vos, v.clf_id]);
  const shgOpts = useMemo(() => opts(shgs.filter((x) => x.vo_id === v.vo_id)), [shgs, v.vo_id]);
  const loanOpts = useMemo(() => loans.filter((l) => l.shg_id === v.shg_id && l.status === "active")
    .map((l) => ({ value: l.id, label: `${l.loan_number} · ${formatINR(l.principal)}` })), [loans, v.shg_id]);
  const loan = loans.find((l) => l.id === v.loan_id);
  const member = loan?.member_id ? members.find((m) => m.id === loan.member_id) : null;
  const nd = sched?.next_due;
  const over = sched && Number(v.amount) > sched.outstanding_total;

  const validate = () => {
    const e = {};
    if (!v.loan_id) e.loan_id = "Select a loan";
    if (!v.payment_date) e.payment_date = "Required";
    else if (v.payment_date > today()) e.payment_date = "Cannot be in the future";
    else if (loan && v.payment_date < loan.disbursement_date) e.payment_date = "Cannot be before disbursement";
    const a = Number(v.amount);
    if (!(a > 0)) e.amount = "Enter an amount greater than ₹0";
    else if (!/^\d+(\.\d{1,2})?$/.test(String(v.amount))) e.amount = "Up to 2 decimal places";
    else if (over && !v.override) e.amount = `Exceeds outstanding ${formatINR(sched.outstanding_total)} — authorised override needed`;
    if (v.collected_by.trim().length < 2) e.collected_by = "Required";
    if (over && v.override) {
      if (v.authorised_by.trim().length < 2) e.authorised_by = "Required for override";
      if (v.override_reason.trim().length < 5) e.override_reason = "Give a reason (min 5 chars)";
    }
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (saving || !validate()) return;
    setSaving(true);
    try {
      const { clf_id, vo_id, shg_id, ...rest } = v;
      const rep = await onSubmit({ ...rest, amount: Number(v.amount), override: !!(over && v.override), idempotency_key: key });
      onOpenChange(false);
      onSaved(rep);
    } catch (err) { toast.error(err.message || "Could not record repayment"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-white" data-testid="repayment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-emerald-950">Record repayment</DialogTitle>
          <DialogDescription>Receipt number is generated automatically when you save.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Receipt number" full><Input disabled value="Auto-generated on save (RCPT-YYYY-NNNNN)" /></Field>
          <Field label="CLF" required>
            <Pick testId="repayment-dialog-clf" value={v.clf_id} placeholder="Select CLF" options={opts(clfs)}
              onChange={(x) => setV((p) => ({ ...p, clf_id: x, vo_id: "", shg_id: "", loan_id: "" }))} />
          </Field>
          <Field label="Village Organization" required>
            <Pick testId="repayment-dialog-vo" value={v.vo_id} placeholder={v.clf_id ? "Select VO" : "Select CLF first"} options={voOpts} disabled={!voOpts.length}
              onChange={(x) => setV((p) => ({ ...p, vo_id: x, shg_id: "", loan_id: "" }))} />
          </Field>
          <Field label="Self Help Group" required>
            <Pick testId="repayment-dialog-shg" value={v.shg_id} placeholder={v.vo_id ? "Select SHG" : "Select VO first"} options={shgOpts} disabled={!shgOpts.length}
              onChange={(x) => setV((p) => ({ ...p, shg_id: x, loan_id: "" }))} />
          </Field>
          <Field label="Loan ID" required error={errors.loan_id}>
            <Pick testId="repayment-dialog-loan" value={v.loan_id} options={loanOpts} disabled={!loanOpts.length}
              placeholder={v.shg_id ? (loanOpts.length ? "Select loan" : "No active loans") : "Select SHG first"} onChange={(x) => set("loan_id", x)} />
          </Field>
          <Field label="Member / borrower" full><Input disabled value={loan ? (member ? `${member.name} (${member.code})` : "SHG (group loan)") : "—"} data-testid="repayment-dialog-member" /></Field>

          {v.loan_id && (
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-emerald-900/15 bg-emerald-50/60 p-4 sm:col-span-2 sm:grid-cols-3" data-testid="repayment-dialog-loan-summary">
              {schedLoading || !sched ? <div className="col-span-3 text-sm text-emerald-900/60"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading schedule…</div> : <>
                <Stat label="Monthly EMI" value={formatINR(loan?.emi)} />
                <Stat label="Previously paid" value={formatINR(sched.total_received)} testId="repayment-dialog-prev-paid" />
                <Stat label="Outstanding (incl. interest)" value={formatINR(sched.outstanding_total)} testId="repayment-dialog-outstanding" />
                <Stat label="Outstanding principal" value={formatINR(sched.outstanding_principal)} />
                <Stat label="EMI due date" value={nd ? `#${nd.no} · ${formatIndianDate(nd.due_date)}` : "All paid"} testId="repayment-dialog-due-date" />
                <Stat label="EMI amount due" value={nd ? formatINR(nd.remaining) : "—"} testId="repayment-dialog-due-amount" />
                {sched.overdue_amount > 0 && <Stat warn label="Overdue" value={formatINR(sched.overdue_amount)} testId="repayment-dialog-overdue" />}
              </>}
            </div>
          )}

          <Field label="Actual payment date" required error={errors.payment_date}>
            <Input type="date" max={today()} value={v.payment_date} onChange={(e) => set("payment_date", e.target.value)} data-testid="repayment-dialog-date" />
          </Field>
          <Field label="Amount received (₹)" required error={errors.amount}>
            <Input type="number" min="0.01" step="0.01" value={v.amount} onChange={(e) => set("amount", e.target.value)}
              placeholder={nd ? String(nd.remaining) : ""} data-testid="repayment-dialog-amount" />
          </Field>
          <Field label="Payment mode" required>
            <Pick testId="repayment-dialog-mode" value={v.mode} options={MODES} onChange={(x) => set("mode", x)} />
          </Field>
          <Field label="Transaction / reference no.">
            <Input value={v.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Optional" data-testid="repayment-dialog-reference" />
          </Field>
          <Field label="Collected by" required error={errors.collected_by}>
            <Input value={v.collected_by} onChange={(e) => set("collected_by", e.target.value)} data-testid="repayment-dialog-collected-by" />
          </Field>
          <Field label="Remarks">
            <Input value={v.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="repayment-dialog-remarks" />
          </Field>

          {over && (
            <div className="space-y-3 rounded-xl border border-rose-300 bg-rose-50 p-4 sm:col-span-2" data-testid="repayment-dialog-override">
              <div className="flex items-start gap-2 text-sm text-rose-800"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                Amount is more than the outstanding balance ({formatINR(sched.outstanding_total)}). An authorised override is required.</div>
              <label className="flex items-center gap-2 text-sm text-rose-900">
                <Checkbox checked={v.override} onCheckedChange={(c) => set("override", !!c)} data-testid="repayment-dialog-override-check" />
                I have authorisation to accept the excess amount
              </label>
              {v.override && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Authorised by" required error={errors.authorised_by}>
                  <Input value={v.authorised_by} onChange={(e) => set("authorised_by", e.target.value)} data-testid="repayment-dialog-authorised-by" /></Field>
                <Field label="Override reason" required error={errors.override_reason}>
                  <Input value={v.override_reason} onChange={(e) => set("override_reason", e.target.value)} data-testid="repayment-dialog-override-reason" /></Field>
              </div>}
            </div>
          )}

          <DialogFooter className="mt-1 sm:col-span-2">
            <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)} data-testid="repayment-dialog-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || schedLoading} className="bg-[#0F4C3A] text-white hover:bg-emerald-900" data-testid="repayment-dialog-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save repayment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
